/**
 * The client environment (#10718): where Finpilot staff work inside one
 * client organization, as the collaborator account a grant activates.
 *
 * It lives on the app's own address, one browser tab at a time, the way
 * Zendesk's "Assume identity" and Salesforce's "Log in as" do (slice 3). A
 * tab is either home (the staff member's own account: dogfooding, the admin
 * console) or in a client. In a client every request carries the grant's
 * bearer token instead of the Stytch session, and every request type (HTTP,
 * identity, websockets) follows it without per-call plumbing, because
 * `getSessionToken` is the one place a credential is read.
 *
 * Two pieces of browser state decide it:
 *
 * - The grant's token, in `localStorage`. The server keeps one token per
 *   grant and redeeming a code replaces it, so every client tab of this
 *   browser must share it: per-tab tokens would make a second tab end the
 *   first one's access.
 * - This tab's mark, in `sessionStorage`. It says which tabs use the token. A
 *   link opened in a new tab starts without it, at home, where the account
 *   menu offers to continue in the client.
 *
 * The mode is decided once, when the page loads, and holds until the next
 * full load, and so does the token a client page uses: it is the one the
 * browser held then, sent only while the browser still holds it. If the
 * token goes away or is replaced mid-page (the grant ended, or another tab
 * opened a client), a client page sends no credential at all and shows that
 * its access ended. It never quietly becomes the staff member's own session,
 * or another client, with this client's data still on screen.
 *
 * Design record: .claude/features/10718-staff-access-architecture/README.md,
 * slice-3-same-address.md.
 */

/** Where the grant's token lives: shared by this browser's client tabs.
 * The token dies with the grant on the server (twelve hours from opening,
 * renewable to at most twenty-four); ending access clears it here, from
 * inside or by logging out, which ends the open grant. */
export const CLIENT_ENVIRONMENT_TOKEN_KEY = "fp_client_environment_token";

/** The grant the held token belongs to, written with it at entry: what
 * tells whether this browser's token is the staff member's open grant, and
 * which client a client tab is in. */
const HELD_GRANT_KEY = "fp_client_environment_grant";

export interface HeldGrant {
  grantId: number;
  organizationId: string;
  organizationName: string;
}

/** This tab's mark: present while the tab is meant to be in a client. */
const TAB_MARK_KEY = "fp_client_environment_tab";

/** The pages that always run at home, even in a marked tab: opening a
 * client, taking admin access and redeeming an entry link need the staff
 * member's own session, and logging out is the staff member's. */
const HOME_PATHS: readonly string[] = [
  "/client-environment/open",
  "/client-environment/enter",
  "/logout",
];

function onPath(paths: readonly string[]): boolean {
  const pathname = window.location.pathname.replace(/\/+$/, "");
  return paths.includes(pathname);
}

function tabIsMarked(): boolean {
  try {
    return sessionStorage.getItem(TAB_MARK_KEY) === "1";
  } catch {
    return false;
  }
}

// Page-scoped by design: the mode, and in a client tab the token and grant
// it loaded with, are read once and hold for the life of the page (see the
// module comment). The story and test harnesses reset them.
let tabMode: boolean | null = null;
let tabToken: string | null = null;
let tabGrant: HeldGrant | null = null;

/** Whether this page is in a client. Decided on first read. */
export function isClientEnvironment(): boolean {
  if (tabMode === null) {
    const token = getClientEnvironmentToken();
    tabMode = !onPath(HOME_PATHS) && tabIsMarked() && token !== null;
    tabToken = tabMode ? token : null;
    tabGrant = tabMode ? heldGrant() : null;
  }
  return tabMode;
}

/** The credential a client page sends: the token it loaded with, while this
 * browser still holds it; null once it is gone or replaced, and null at
 * home. */
export function clientTabToken(): string | null {
  if (!isClientEnvironment()) return null;
  return getClientEnvironmentToken() === tabToken ? tabToken : null;
}

/** The client this client page loaded in, or null (at home, or entered
 * before the grant was recorded beside the token). */
export function tabClient(): HeldGrant | null {
  return isClientEnvironment() ? tabGrant : null;
}

/** Forget the decided mode, so the next read decides again (tests, stories). */
export function resetClientEnvironmentMode(): void {
  tabMode = null;
  tabToken = null;
  tabGrant = null;
}

/** Mark this tab for the client; it takes effect at the next full load. */
export function markTabForClient(): void {
  try {
    sessionStorage.setItem(TAB_MARK_KEY, "1");
  } catch {
    // Blocked storage: the tab stays at home and says so on loading.
  }
}

/** Return this tab to home at the next full load. */
export function unmarkTab(): void {
  try {
    sessionStorage.removeItem(TAB_MARK_KEY);
  } catch {
    // See markTabForClient.
  }
}

export function getClientEnvironmentToken(): string | null {
  try {
    return localStorage.getItem(CLIENT_ENVIRONMENT_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Keep `token` for this browser's client tabs, with the grant it belongs
 * to (none: a token whose grant is unknown, which Continue never trusts).
 * The grant is written first, so a tab that sees the new token sees its
 * grant too. */
export function setClientEnvironmentToken(
  token: string,
  grant: HeldGrant | null = null
): void {
  try {
    if (grant === null) localStorage.removeItem(HELD_GRANT_KEY);
    else localStorage.setItem(HELD_GRANT_KEY, JSON.stringify(grant));
    localStorage.setItem(CLIENT_ENVIRONMENT_TOKEN_KEY, token);
  } catch {
    // Blocked storage: the environment cannot keep its access, and the
    // ended page explains how to open it again.
  }
}

export function clearClientEnvironmentToken(): void {
  try {
    localStorage.removeItem(CLIENT_ENVIRONMENT_TOKEN_KEY);
    localStorage.removeItem(HELD_GRANT_KEY);
  } catch {
    // See setClientEnvironmentToken.
  }
}

/** The grant this browser's token belongs to, or null. */
export function heldGrant(): HeldGrant | null {
  try {
    const raw = localStorage.getItem(HELD_GRANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HeldGrant>;
    return typeof parsed.grantId === "number" &&
      typeof parsed.organizationId === "string" &&
      typeof parsed.organizationName === "string"
      ? {
          grantId: parsed.grantId,
          organizationId: parsed.organizationId,
          organizationName: parsed.organizationName,
        }
      : null;
  } catch {
    return null;
  }
}

/** Whether this browser holds the token of `grantId`, the staff member's
 * open grant: only then may a tab join it without drawing a new code. A
 * token left by an ended grant, or by someone else's grant on a shared
 * browser, is never reused. */
export function holdsTokenFor(grantId: number | null | undefined): boolean {
  return (
    grantId != null &&
    getClientEnvironmentToken() !== null &&
    heldGrant()?.grantId === grantId
  );
}

/** Whether Sentry may record session replay here.
 *
 * Not in a client tab, and not on the entry page: replay records request and
 * response bodies for our own origin, and the code exchange's response is
 * the collaborator's token. Error reporting stays on. */
export function recordsSessionReplay(): boolean {
  return !isClientEnvironment() && !onPath([CLIENT_ENVIRONMENT_ENTER_PATH]);
}

/** Leave the client for home: a full load at `path` (the app's root by
 * default), which decides the tab's mode again. */
export function goHome(path: string = "/"): void {
  unmarkTab();
  window.location.replace(path);
}

// Opening a client writes an access record the client sees, so the open page
// acts on its own only when this tab's own click asked it to (review 1b M1).
// An external link cannot write this site's sessionStorage. The arming
// expires, so one left behind by a navigation that never happened cannot
// open the client for a link followed later in the same tab.
const AUTO_OPEN_KEY = "fp_client_environment_auto_open";
export const AUTO_OPEN_WINDOW_MS = 60_000;

/** Let the open page open `organizationId` without a further click, once,
 * within AUTO_OPEN_WINDOW_MS. */
export function armAutoOpen(organizationId: string): void {
  try {
    sessionStorage.setItem(
      AUTO_OPEN_KEY,
      JSON.stringify({ organizationId, at: Date.now() })
    );
  } catch {
    // Blocked storage: the open page asks for a click.
  }
}

/** Whether auto-open was armed for `organizationId` and is still fresh;
 * disarms it either way. */
export function takeAutoOpen(organizationId: string): boolean {
  try {
    const raw = sessionStorage.getItem(AUTO_OPEN_KEY);
    sessionStorage.removeItem(AUTO_OPEN_KEY);
    if (!raw) return false;
    const armed = JSON.parse(raw) as { organizationId?: unknown; at?: unknown };
    return (
      armed.organizationId === organizationId &&
      typeof armed.at === "number" &&
      Date.now() - armed.at <= AUTO_OPEN_WINDOW_MS
    );
  } catch {
    return false;
  }
}

/** The page that trades a one-time code for the collaborator's token. */
export const CLIENT_ENVIRONMENT_ENTER_PATH = "/client-environment/enter";

/** The page that opens a client environment, as the staff member: with
 * `?org=` it opens that org and sends the tab there; without, it goes to the
 * admin console. */
export const CLIENT_ENVIRONMENT_OPEN_PATH = "/client-environment/open";

/** The staff console, which belongs to the staff member's home: the one
 * place a client environment is opened from, and never part of one. */
export const STAFF_CONSOLE_PATH = "/__admin";

/** Whether `pathname` is the staff console or a page under it. */
export function isStaffConsolePath(pathname: string): boolean {
  return (
    pathname === STAFF_CONSOLE_PATH ||
    pathname.startsWith(`${STAFF_CONSOLE_PATH}/`)
  );
}

/** A same-origin path to land on, or null.
 *
 * Judged the way the browser will read it, not by its first characters:
 * "/\\host", "/<tab>/host" and "/<newline>/host" all parse as "//host", an
 * address on another origin (review 1b M1), and "/.//host" normalises to it. */
export function safeNextPath(next: string | null | undefined): string | null {
  if (!next || !next.startsWith("/")) return null;
  const base = "https://same-origin.invalid";
  let url: URL;
  try {
    url = new URL(next, base);
  } catch {
    return null;
  }
  const path = `${url.pathname}${url.search}${url.hash}`;
  // Normalising dot segments can itself produce "//host" ("/.//host"), which
  // a navigation reads as another origin.
  if (url.origin !== base || path.startsWith("//")) return null;
  return path;
}

/** What an entry link carried: the one-time code and where to land. */
export interface EntryLink {
  code: string;
  /** As the link gave it; the entry page judges it with `safeNextPath`. */
  next: string | null;
}

// Page-scoped by design: the link is read out of the address once, when the
// page loads, and the address is then rewritten without it, so memory is the
// only place left for the entry page to find the code. It lives exactly as
// long as the page does (a full load follows a successful exchange). The
// story harness resets it before every story (`.storybook/vitest.setup.ts`).
let entryLink: EntryLink | null = null;

/** Take an entry link's one-time code out of the address.
 *
 * On the entry page, reads `code` and `next` from the fragment into memory
 * and replaces the history entry with the same address without the fragment.
 * The browser never sends a fragment to a server, but everything that runs in
 * the page can read it: error reporting records the page's address in its
 * pageload trace and on every event, and the entry stays in the tab's
 * history. So this runs at boot, before anything else is initialized, and
 * again when the entry page mounts. Any other page's fragment is left alone.
 * Safe to call again: with the address already clean it keeps what it took. */
export function captureEntryLink(): void {
  const { pathname, search, hash } = window.location;
  if (pathname.replace(/\/+$/, "") !== CLIENT_ENVIRONMENT_ENTER_PATH) return;
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const code = fragment.get("code");
  if (!code) return;
  entryLink = { code, next: fragment.get("next") };
  window.history.replaceState(window.history.state, "", `${pathname}${search}`);
}

/** The entry link taken from the address, or null when there was none. */
export function capturedEntryLink(): EntryLink | null {
  return entryLink;
}

/** Hold `link` as if it had been taken from the address (stories, tests). */
export function rememberEntryLink(link: EntryLink): void {
  entryLink = link;
}

export function resetEntryLink(): void {
  entryLink = null;
}

/** The entry link for a one-time code.
 *
 * The code rides in the fragment, which the browser never sends to a server,
 * so it cannot land in a static host's access log. Script in the page can
 * still read a fragment, which is why the entry page takes it out of the
 * address on arrival (`captureEntryLink`). */
export function clientEnvironmentEnterUrl(
  code: string,
  next?: string | null
): string {
  const fragment = new URLSearchParams({ code });
  const landing = safeNextPath(next);
  if (landing) fragment.set("next", landing);
  return `${CLIENT_ENVIRONMENT_ENTER_PATH}#${fragment.toString()}`;
}

/** The page that opens `organizationId`, landing on `next` inside it. With
 * `elevate`, the page that takes admin access in that open client. */
export function clientEnvironmentOpenUrl(
  organizationId: string,
  next?: string | null,
  options: { elevate?: boolean } = {}
): string {
  const query = new URLSearchParams();
  query.set("org", organizationId);
  if (options.elevate) query.set("elevate", "1");
  const landing = safeNextPath(next);
  if (landing) query.set("next", landing);
  return `${CLIENT_ENVIRONMENT_OPEN_PATH}?${query.toString()}`;
}
